import React, { useMemo, useState } from "react";
import { increment, push, ref, set, update } from "firebase/database";
import { auth, database } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import { TRANSACTION_DIRECTION } from "../../../constants/farmerConstants";
import {
  BALANCE_ADJUSTMENT_MODE,
  buildBalanceAdjustmentDetails,
  calculateBalanceAdjustment,
} from "./farmerBalanceAdjustmentHelpers";

const FarmerBalanceAdjustmentModal = ({
  farmer,
  companyName,
  currentBalance,
  onClose,
  onSaved,
}) => {
  const [adjustmentMode, setAdjustmentMode] = useState(
    BALANCE_ADJUSTMENT_MODE.ADD
  );
  const [amount, setAmount] = useState("");
  const [details, setDetails] = useState("");
  const [saving, setSaving] = useState(false);

  const parsedCurrentBalance = Number.parseFloat(currentBalance || 0) || 0;

  const preview = useMemo(() => {
    if (amount === "") return null;
    try {
      return calculateBalanceAdjustment(parsedCurrentBalance, adjustmentMode, amount);
    } catch (error) {
      return { error: error.message };
    }
  }, [parsedCurrentBalance, adjustmentMode, amount]);

  const handleSave = async () => {
    let computedAdjustment;
    try {
      computedAdjustment = calculateBalanceAdjustment(
        parsedCurrentBalance,
        adjustmentMode,
        amount
      );
    } catch (error) {
      toast.error(error.message);
      return;
    }

    setSaving(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("No authenticated user found");

      const now = new Date();
      const year = now.getFullYear();
      const month = now.toLocaleString("en-US", { month: "long" });
      const day = now.getDate();
      const dateStr = now.toISOString();
      const paymentId = push(ref(database)).key;

      const previousBalance = parsedCurrentBalance.toString();
      const newBalance = computedAdjustment.newBalance.toString();
      const detailsSummary = buildBalanceAdjustmentDetails({
        mode: adjustmentMode,
        previousBalance,
        newBalance,
        userDetails: details,
      });

      const paymentData = {
        Id: paymentId,
        OtherName: farmer?.otherName || "",
        CloudName: farmer?.name || "",
        Status: "Approved",
        Details: detailsSummary,
        CompanyName: companyName,
        AuthorizedBy: user.email,
        TransactionDate: dateStr,
        Amount: Math.abs(computedAdjustment.delta).toString(),
        ReceiptNo: `RCPT-${paymentId}`,
        Mode: "Balance Adjustment",
        AdjustmentMode: adjustmentMode,
        PreviousBalance: previousBalance,
        NewBalance: newBalance,
      };

      const writePromises = [];

      const farmerBalancePath = `Companies/${companyName}/ValidatedFarmers/${farmer.otherName}`;
      writePromises.push(
        update(ref(database, farmerBalancePath), {
          Balance: increment(computedAdjustment.delta),
        })
      );

      const farmerPaymentPath = `Companies/${companyName}/FarmerRecords/${farmer.otherName}/Payments/${year}/${month}/${paymentId}`;
      writePromises.push(set(ref(database, farmerPaymentPath), paymentData));

      const companyPaymentPath = `Companies/${companyName}/Payments/${year}/${month}/${day}/${paymentId}`;
      writePromises.push(set(ref(database, companyPaymentPath), paymentData));

      const payableTxPath = `Companies/${companyName}/Accounts/farmer_payable/${year}/${month}/${paymentId}`;
      const payableTxData = {
        id: paymentId,
        amount: Math.abs(computedAdjustment.delta),
        direction:
          computedAdjustment.delta >= 0
            ? TRANSACTION_DIRECTION.CREDIT
            : TRANSACTION_DIRECTION.DEBIT,
        farmerName: farmer?.name || "",
        farmerOtherName: farmer?.otherName || "",
        description: `Balance adjustment for ${
          farmer?.name || farmer?.otherName || "Unknown Farmer"
        }`,
        details: detailsSummary,
        previousBalance: previousBalance,
        newBalance: newBalance,
        date: dateStr,
        processedBy: user.email,
      };
      writePromises.push(set(ref(database, payableTxPath), payableTxData));

      await Promise.all(writePromises);
      toast.success("Farmer balance updated and transaction saved");
      onSaved?.();
      onClose();
    } catch (error) {
      console.error("Failed to adjust farmer balance:", error);
      toast.error("Failed to adjust farmer balance");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-orange-700 mb-4">
          Change Farmer Balance
        </h3>

        <div className="mb-4 p-3 bg-orange-50 rounded-lg">
          <p className="text-sm text-gray-600">Current Balance</p>
          <p className="text-2xl font-bold text-orange-700">
            KES {parsedCurrentBalance.toLocaleString()}
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Action</label>
            <select
              className="border rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={adjustmentMode}
              onChange={(event) => setAdjustmentMode(event.target.value)}
              disabled={saving}
            >
              <option value={BALANCE_ADJUSTMENT_MODE.ADD}>Add to balance</option>
              <option value={BALANCE_ADJUSTMENT_MODE.SUBTRACT}>
                Subtract from balance
              </option>
              <option value={BALANCE_ADJUSTMENT_MODE.SET}>
                Set actual balance
              </option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              {adjustmentMode === BALANCE_ADJUSTMENT_MODE.SET
                ? "Actual balance (KES)"
                : "Amount (KES)"}
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="border rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="Enter value"
              disabled={saving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Details</label>
            <textarea
              className="border rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-orange-500"
              rows="3"
              value={details}
              onChange={(event) => setDetails(event.target.value)}
              placeholder="Why is this balance change needed?"
              disabled={saving}
            />
          </div>

          {preview && (
            <div className="p-3 bg-gray-50 rounded-lg">
              {preview.error ? (
                <p className="text-sm text-red-600">{preview.error}</p>
              ) : (
                <>
                  <p className="text-sm text-gray-600">New Balance</p>
                  <p className="text-xl font-bold text-orange-700">
                    KES {preview.newBalance.toLocaleString()}
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className={`px-4 py-2 rounded-md text-white ${
              saving ? "bg-orange-300 cursor-not-allowed" : "bg-orange-600 hover:bg-orange-700"
            }`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FarmerBalanceAdjustmentModal;
